import React, { useEffect, useRef, useState } from "react";
import {
  FaChevronDown,
  FaFilter,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { SITE_STATUS, STATUTS_CANON } from "../constants";
import { getDistinctDepartements } from "../../../../../services/prospectionServices";

export default function FiltersBar({
  search,
  setSearch,
  discipline,
  setDiscipline,
  departement,
  setDepartement,
  statuts,
  setStatuts,
  siteStatus,
  setSiteStatus,
  emailMode,
  setEmailMode,
  licMin,
  setLicMin,
  licMax,
  setLicMax,
  sort,
  setSort,
  disciplines,
  onApply,
  onReset,
  limit,
  setLimit,
  total,
  hideUnsubStatus,
  setHideUnsubStatus,
}) {
  // ---- Autocomplete Département / Zone ----
  const [depInput, setDepInput] = useState(departement || "");
  const [depOptions, setDepOptions] = useState([]);
  const [openDep, setOpenDep] = useState(false);
  const depBoxRef = useRef(null);
  const depDebounceRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (!depBoxRef.current) return;
      if (!depBoxRef.current.contains(e.target)) setOpenDep(false);
    };

    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (depDebounceRef.current) clearTimeout(depDebounceRef.current);

    depDebounceRef.current = setTimeout(async () => {
      const q = depInput.trim();

      if (!q) {
        setDepOptions([]);
        return;
      }

      try {
        const list = await getDistinctDepartements({ q, limit: 20 });
        setDepOptions(list || []);
      } catch {
        setDepOptions([]);
      }
    }, 250);

    return () => depDebounceRef.current && clearTimeout(depDebounceRef.current);
  }, [depInput]);

  useEffect(() => {
    setDepInput(departement || "");
  }, [departement]);

  const optionValue = (opt) => {
    if (typeof opt === "string") return opt;
    return opt?.departement_code || opt?.code || opt?.value || "";
  };

  const selectDep = (code) => {
    setDepInput(code);
    setDepartement(code);
    setOpenDep(false);
  };

  const clearDep = () => {
    setDepInput("");
    setDepartement("");
    setDepOptions([]);
    setOpenDep(false);
  };

  const onDepKeyDown = (e) => {
    if (e.key === "Enter") {
      const exact = depOptions
        .map(optionValue)
        .find((o) => o === depInput.trim());

      if (exact) selectDep(exact);

      setOpenDep(false);
      onApply && onApply();
    }
  };

  const selectedStatusCount = Array.isArray(statuts) ? statuts.length : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
        <div>
          <div className="text-sm font-semibold flex items-center gap-2">
            <FaFilter /> Filtres & tri
          </div>

          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Combinez plusieurs critères : discipline, département, site, emails,
            licenciés et plusieurs statuts.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={emailMode}
            onChange={(e) => setEmailMode(e.target.value)}
            className="px-2 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
            title="Filtrer sur la présence de l'email"
          >
            <option value="all">Email : Tous</option>
            <option value="with">Email : Avec</option>
            <option value="without">Email : Sans</option>
          </select>

          <label
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm cursor-pointer select-none"
            title='Masquer les lignes ayant le statut "Désinscrit"'
          >
            <input
              type="checkbox"
              checked={hideUnsubStatus}
              onChange={(e) => setHideUnsubStatus(e.target.checked)}
            />
            Masquer “Désinscrit”
          </label>

          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="px-2 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
          >
            {[25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={onReset}
            className="px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm"
          >
            Réinitialiser
          </button>

          <button
            type="button"
            onClick={onApply}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 hover:opacity-90 text-sm font-medium"
          >
            Appliquer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <FaSearch className="opacity-60" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Recherche club, email, contact, tél, notes"
              className="bg-transparent outline-none w-full text-sm"
            />
          </div>
        </div>

        <div className="lg:col-span-2">
          <select
            value={discipline}
            onChange={(e) => setDiscipline(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
          >
            <option value="">Discipline</option>
            {disciplines.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <div className="lg:col-span-2" ref={depBoxRef}>
          <div className="relative">
            <input
              value={depInput}
              onChange={(e) => {
                setDepInput(e.target.value);
                setOpenDep(true);
              }}
              onFocus={() => setOpenDep(true)}
              onKeyDown={onDepKeyDown}
              placeholder="Département / Zone"
              className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm pr-8"
            />

            {depInput ? (
              <button
                type="button"
                title="Effacer"
                onClick={clearDep}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <FaTimes />
              </button>
            ) : null}

            {openDep && depOptions.length > 0 && (
              <div className="absolute z-50 mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg max-h-60 overflow-auto">
                {depOptions.map((opt) => {
                  const val = optionValue(opt);
                  if (!val) return null;

                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => selectDep(val)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      {val}
                    </button>
                  );
                })}
              </div>
            )}

            {openDep && depInput && depOptions.length === 0 && (
              <div className="absolute z-50 mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg">
                <div className="px-3 py-2 text-sm text-slate-500">
                  Aucun résultat
                </div>
              </div>
            )}
          </div>

          <div className="text-[11px] text-slate-500 mt-1">
            Exemples : 33, 75, BE-BRU…
          </div>
        </div>

        <div className="lg:col-span-2">
          <MultiStatusSelect value={statuts} onChange={setStatuts} />
        </div>

        <div className="lg:col-span-2">
          <select
            value={siteStatus}
            onChange={(e) => setSiteStatus(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
          >
            <option value="">Site</option>
            {SITE_STATUS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="lg:col-span-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              inputMode="numeric"
              min="0"
              placeholder="Lic. min"
              value={licMin}
              onChange={(e) => setLicMin(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
            />

            <input
              type="number"
              inputMode="numeric"
              min="0"
              placeholder="Lic. max"
              value={licMax}
              onChange={(e) => setLicMax(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
            />
          </div>

          <div className="text-[11px] text-slate-500 mt-1">
            Fourchette de licenciés
          </div>
        </div>

        <div className="lg:col-span-1">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
          >
            <option value="-updated_at">Tri : récent</option>
            <option value="nom_club">Nom A→Z</option>
            <option value="-nom_club">Nom Z→A</option>
            <option value="discipline">Discipline</option>
            <option value="departement_code">Département/Zone</option>
            <option value="nb_licencies">Licenciés croissant</option>
            <option value="-nb_licencies">Licenciés décroissant</option>
            <option value="statut">Statut</option>
            <option value="-date_contact">Dernier contact</option>
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span>Total : {total} fiche(s)</span>

        {selectedStatusCount > 0 && (
          <span className="rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200 px-2 py-1">
            {selectedStatusCount} statut(s) sélectionné(s)
          </span>
        )}
      </div>
    </div>
  );
}

function MultiStatusSelect({ value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const selected = Array.isArray(value) ? value : [];

  useEffect(() => {
    const onClick = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };

    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = (status) => {
    if (selected.includes(status)) {
      onChange(selected.filter((s) => s !== status));
    } else {
      onChange([...selected, status]);
    }
  };

  const label =
    selected.length === 0
      ? "Statuts : Tous"
      : selected.length === 1
        ? selected[0]
        : `${selected.length} statuts`;

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm flex items-center justify-between gap-2"
      >
        <span className="truncate">{label}</span>
        <FaChevronDown className="text-xs opacity-60" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-72 max-w-[80vw] rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Statuts
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onChange(STATUTS_CANON)}
                className="text-xs px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                Tous
              </button>

              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                Aucun
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-1 max-h-72 overflow-auto">
            {STATUTS_CANON.map((status) => {
              const checked = selected.includes(status);

              return (
                <label
                  key={status}
                  className={[
                    "flex items-center gap-2 px-2 py-2 rounded-xl cursor-pointer text-sm",
                    checked
                      ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(status)}
                    className="h-4 w-4 accent-indigo-600"
                  />
                  <span>{status}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}